import { useState, useEffect, useRef } from 'react';
import { useUser, useClerk, useReverification } from '@clerk/clerk-react';
import { isReverificationCancelledError } from '@clerk/clerk-react/errors';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useClerkAuth } from '../../../hooks/useClerkAuth';
import { useDarkMode } from '../../../hooks/useDarkMode';
import { useApp } from '../../../contexts/AppContext';
import { CANVA_OAUTH_SCOPES } from '../../../constants/canvaScopes';
import { unlinkCanva } from '../../../services/canvaLink';
import {
  Palette,
  Moon,
  Sun,
  User,
  Link as LinkIcon,
  Unlink,
  CheckCircle,
  ExternalLink,
  LogOut,
  Library,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '../../ui/Card';
import Button from '../../ui/Button';
import { useToast } from '../../ui/Toast';
import BentoGrid, { BentoItem } from '../../layouts/BentoGrid';
import { motion } from 'framer-motion';
import { haptics } from '../../../utils/haptics';
import AutoSyncCard from './AutoSyncCard';
import PlanCard from './PlanCard';
import { usePricingV2 } from '../../../hooks/usePricingV2';

export default function Settings() {
  const { user, isLoaded } = useUser();
  const clerk = useClerk();
  const navigate = useNavigate();
  const pricingV2 = usePricingV2();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasCanvaConnected } = useClerkAuth();
  const { isDark, toggle: toggleDarkMode } = useDarkMode();
  const { state: appState } = useApp();
  const canvaAuthFailure = appState.canvaAuthFailure;
  const toast = useToast();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const reauthRequired = location.state?.reauthRequired === true;
  const isCanvaConnected = reauthRequired ? false : hasCanvaConnected();
  const wasConnectedRef = useRef(isCanvaConnected);
  const autoConnectFiredRef = useRef(false);

  const createCanvaAccount = useReverification(
    () => user.createExternalAccount({
      strategy: 'oauth_custom_canva2',
      redirectUrl: `${window.location.origin}/dashboard`,
      additionalScopes: CANVA_OAUTH_SCOPES,
    })
  );

  // Destroying an external account is a sensitive operation: Clerk requires
  // step-up reverification when the session hasn't been freshly verified.
  // Wrapping the destroy in useReverification makes Clerk show its built-in
  // verification modal and auto-retry on success, instead of the destroy
  // rejecting with `reverification_required`. Shared by both the connect flow
  // (which removes any prior connection before re-linking) and the disconnect flow.
  const disconnectCanvaAccount = useReverification((options) => unlinkCanva(user, options));

  const handleConnectCanva = async () => {
    setIsConnecting(true);
    try {
      // Remove any existing Canva connection first (e.g. re-linking or switching
      // Canva apps). Uses the reverification-wrapped destroy above so Clerk's
      // verification modal shows, instead of the delete rejecting with
      // `reverification_required` and leaving the user stuck.
      await disconnectCanvaAccount();

      const externalAccount = await createCanvaAccount();

      const redirectUrl = externalAccount.verification?.externalVerificationRedirectURL;
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    } catch (error) {
      // User dismissed the reverification modal — not a failure, don't alarm them.
      if (isReverificationCancelledError(error)) {
        setIsConnecting(false);
        return;
      }
      console.error('Failed to connect Canva:', error);
      toast.error('Failed to connect Canva. Please try again.');
      setIsConnecting(false);
    }
  };

  const handleDisconnectCanva = async () => {
    setIsDisconnecting(true);
    haptics.light();
    try {
      await disconnectCanvaAccount({ markDisconnected: true });
      toast.success('Canva account disconnected.');
      haptics.success();
    } catch (error) {
      // User dismissed the reverification modal — not a failure, don't alarm them.
      if (isReverificationCancelledError(error)) {
        return;
      }
      console.error('Failed to disconnect Canva:', error);
      toast.error('Failed to disconnect Canva. Please try again.');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleToggleDarkMode = () => {
    toggleDarkMode();
    haptics.light();
    toast.success(`${isDark ? 'Light' : 'Dark'} mode enabled`);
  };

  const handleLogout = async () => {
    try {
      haptics.light();
      await clerk.signOut();
      navigate('/sign-in');
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Failed to logout. Please try again.');
    }
  };

  // Auto-redirect to dashboard after successful Canva connection
  useEffect(() => {
    if (!wasConnectedRef.current && isCanvaConnected) {
      toast.success('Canva connected successfully! Redirecting to dashboard...');
      haptics.success();
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    }
    wasConnectedRef.current = isCanvaConnected;
  }, [isCanvaConnected, navigate, toast]);

  // ?action=link-canva deep-link: auto-trigger OAuth on arrival; strip param first to prevent re-fire on back-nav
  useEffect(() => {
    if (searchParams.get('action') !== 'link-canva') return;
    if (isCanvaConnected) {
      setSearchParams({}, { replace: true });
      return;
    }
    if (!isLoaded) return;
    if (autoConnectFiredRef.current) return;
    autoConnectFiredRef.current = true;
    setSearchParams({}, { replace: true });
    handleConnectCanva();
  }, [isLoaded, isCanvaConnected, searchParams, setSearchParams]); // handleConnectCanva intentionally omitted — fired at most once per mount

  return (
    <div className="min-h-screen pb-4 safe-top">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-display text-gray-900 dark:text-gray-100 mb-2">
            Settings
          </h1>
          <p className="text-body text-gray-600 dark:text-gray-400">
            Manage your account and preferences
          </p>
        </div>

        {/* Settings Bento Grid */}
        <BentoGrid>
          {/* Canva Connection Card */}
          <BentoItem size="large">
            <Card 
              variant="elevated" 
              className={isCanvaConnected ? 'ring-2 ring-emerald-500/20' : ''}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 squircle-sm ${
                      isCanvaConnected 
                        ? 'bg-emerald-100 dark:bg-emerald-900/30' 
                        : 'bg-gray-100 dark:bg-elevated-dark-2'
                    }`}>
                      <Palette className={`w-6 h-6 ${
                        isCanvaConnected 
                          ? 'text-emerald-600 dark:text-emerald-400' 
                          : 'text-gray-600 dark:text-gray-400'
                      }`} />
                    </div>
                    <div>
                      <h3 className="text-heading font-semibold text-gray-900 dark:text-gray-100">
                        Canva Integration
                      </h3>
                      <p className="text-body-sm text-gray-600 dark:text-gray-400 mt-1">
                        {isCanvaConnected 
                          ? 'Your Canva account is connected' 
                          : 'Connect to access your designs'}
                      </p>
                    </div>
                  </div>
                  {isCanvaConnected && (
                    <CheckCircle className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  )}
                </div>
              </CardHeader>

              <CardContent>
                {isCanvaConnected ? (
                  <div className="space-y-4">
                    {canvaAuthFailure ? (
                      <div className="p-4 squircle-sm bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50">
                        <p className="text-body-sm font-medium text-amber-900 dark:text-amber-100">
                          Connection issue — please reconnect
                        </p>
                        <p className="text-caption text-amber-700 dark:text-amber-400 mt-1">
                          TurboSync Pro can't access your Canva designs. Disconnect and reconnect to restore sync.
                        </p>
                      </div>
                    ) : (
                      <div className="p-4 squircle-sm bg-emerald-50 dark:bg-emerald-900/20">
                        <p className="text-body-sm font-medium text-emerald-900 dark:text-emerald-100">
                          Connected and syncing
                        </p>
                        <p className="text-caption text-emerald-700 dark:text-emerald-400 mt-1">
                          TurboSync Pro is continuously syncing your designs
                        </p>
                      </div>
                    )}
                    <Button
                      onClick={handleDisconnectCanva}
                      disabled={isDisconnecting}
                      loading={isDisconnecting}
                      variant="secondary"
                      size="md"
                      className="w-full sm:w-auto text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Unlink className="w-4 h-4 mr-2" />
                      {isDisconnecting ? 'Disconnecting…' : 'Disconnect from Canva'}
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={handleConnectCanva}
                    disabled={isConnecting}
                    loading={isConnecting}
                    variant="primary"
                    size="lg"
                    className="w-full sm:w-auto"
                  >
                    <LinkIcon className="w-5 h-5 mr-2" />
                    {isConnecting ? 'Connecting…' : 'Connect to Canva'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </BentoItem>

          {/* Auto-sync Card */}
          <BentoItem size="large">
            <AutoSyncCard />
          </BentoItem>

          {pricingV2.enabled && (
            <BentoItem size="large">
              <PlanCard plan={pricingV2.plan} />
            </BentoItem>
          )}

          {/* ProPresenter library: set per Media Station from its card */}
          <BentoItem size="large">
            <Card variant="elevated" data-testid="station-library-pointer">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 squircle-sm bg-indigo-100 dark:bg-indigo-900/30">
                    <Library className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-heading font-semibold text-gray-900 dark:text-gray-100">
                      ProPresenter library
                    </h3>
                    <p className="text-body-sm text-gray-600 dark:text-gray-400 mt-1">
                      Each Media Station has its own library. Change it from the menu on the station&apos;s card.
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Link
                  to="/media-stations"
                  data-testid="station-library-settings-link"
                  className="inline-flex items-center gap-1 text-body-sm font-medium text-amber-600 dark:text-amber-400 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded"
                >
                  Go to Media Stations
                  <ChevronRight className="w-4 h-4" aria-hidden="true" />
                </Link>
                <Link
                  to="/download"
                  data-testid="station-download-settings-link"
                  className="ml-4 inline-flex items-center gap-1 text-body-sm font-medium text-amber-600 dark:text-amber-400 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded"
                >
                  Download Media Station
                  <ChevronRight className="w-4 h-4" aria-hidden="true" />
                </Link>
              </CardContent>
            </Card>
          </BentoItem>

          {/* Theme Toggle Card */}
          <BentoItem>
            <Card variant="elevated" className="h-full">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 squircle-sm bg-blue-100 dark:bg-blue-900/30">
                    {isDark ? (
                      <Moon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    ) : (
                      <Sun className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-heading font-semibold text-gray-900 dark:text-gray-100">
                      Theme
                    </h3>
                    <p className="text-body-sm text-gray-600 dark:text-gray-400 mt-1">
                      {isDark ? 'Dark mode' : 'Light mode'}
                    </p>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                <button
                  onClick={handleToggleDarkMode}
                  className="relative w-full h-12 squircle-sm bg-gray-200 dark:bg-elevated-dark-2 transition-colors touch-target"
                >
                  <motion.div
                    layout
                    className="absolute top-1 bottom-1 left-1 w-[calc(50%-0.25rem)] squircle-sm bg-white dark:bg-elevated-dark-3 shadow-soft"
                    animate={{
                      x: isDark ? 'calc(100% + 0.5rem)' : 0,
                    }}
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                  <div className="relative flex items-center justify-around h-full px-4">
                    <span className={`text-body-sm font-medium transition-colors ${
                      !isDark ? 'text-gray-900' : 'text-gray-500'
                    }`}>
                      Light
                    </span>
                    <span className={`text-body-sm font-medium transition-colors ${
                      isDark ? 'text-gray-100' : 'text-gray-500'
                    }`}>
                      Dark
                    </span>
                  </div>
                </button>
              </CardContent>
            </Card>
          </BentoItem>

          {/* Account Info Card */}
          <BentoItem size="large">
            <Card variant="elevated">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 squircle-sm bg-purple-100 dark:bg-purple-900/30">
                    <User className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-heading font-semibold text-gray-900 dark:text-gray-100">
                      Account Information
                    </h3>
                    <p className="text-body-sm text-gray-600 dark:text-gray-400 mt-1">
                      Your profile details
                    </p>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="block text-caption text-gray-500 dark:text-gray-400 mb-1">
                      Name
                    </label>
                    <p className="text-body font-medium text-gray-900 dark:text-gray-100">
                      {user?.firstName || user?.fullName || 'Not set'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-caption text-gray-500 dark:text-gray-400 mb-1">
                      Email
                    </label>
                    <p className="text-body font-medium text-gray-900 dark:text-gray-100">
                      {user?.primaryEmailAddress?.emailAddress || 'Not set'}
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={() => clerk.openUserProfile()}
                      variant="secondary"
                      size="sm"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Manage Account
                    </Button>
                    <Button
                      onClick={handleLogout}
                      variant="secondary"
                      size="sm"
                      className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </BentoItem>

          {/* App Info Card */}
          <BentoItem>
            <Card variant="elevated" className="h-full">
              <CardHeader>
                <h3 className="text-heading font-semibold text-gray-900 dark:text-gray-100">
                  About
                </h3>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-body-sm text-gray-600 dark:text-gray-400">
                  <div>
                    <p className="text-gray-900 dark:text-gray-100 font-medium">
                      TurboSync Pro
                    </p>
                    <p className="text-caption">Version 1.0.0</p>
                  </div>
                  <p>
                    Continuous design sync and live-presentation prep for AV teams
                  </p>
                </div>
              </CardContent>
            </Card>
          </BentoItem>
        </BentoGrid>
      </div>
    </div>
  );
}
